<template>
  <!-- prettier-ignore -->
  <div class="query">
    <basic-text-input
      ref="inputRef"
      v-model="selectedLabel"
      manual-validation
      :name="$props.name"
      :disabled="$props.disabled"
      :label="$props.label"
      :required="$props.required"
      :size="$props.size"
      :supporting-text="$props.supportingText"
      :v="$props.v"
      @clear="doClear"
      @keydown="onKeyDown"
      @input.stop="search"
    >
      <template #leading>
        <slot name="leading" />
      </template>

      <template #trailing>
        <the-icon
          v-if="isLoading"
          class="fa-spin"
          art="duo"
          name="spinner-third"
        />
      </template>
    </basic-text-input>

    <basic-dropdown-menu
      :id="dropdownId"
      ref="dropdownRef"
      manual-focus
      name="query-dropdown"
      :options="$props.results"
      :selected="model"
      :size="$props.size"
      @close="onClose"
      @select="selectItem"
    />
  </div>
</template>

<script setup>
  import { onMounted, nextTick, ref, watch } from 'vue'
  import { v4 as uuidv4 } from 'uuid'
  import { debounce } from 'lodash'

  import { ReactivityUtil } from '@shared/utils/Reactivity'

  import BasicDropdownMenu from '@shared/components/Basic/DropdownMenu'
  import BasicTextInput from '@shared/components/Basic/TextInput'

  import { DEFAULT_FORM_DEBOUNCE_TIME } from '@shared/config/constants'

  // INIT
  const emit = defineEmits(['search'])
  const model = defineModel({ type: [Object, String] })
  const props = defineProps({
    disabled: {
      type: Boolean,
      default: false,
    },

    hasTrailing: {
      type: Boolean,
      default: false,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    label: {
      type: String,
      required: true,
    },

    labelKey: {
      type: String,
      default: 'label',
    },

    name: {
      type: String,
      required: true,
    },

    results: {
      type: Array,
      default: () => [],
    },

    required: {
      type: Boolean,
      default: false,
    },

    size: {
      validator(value) {
        return ['sm', 'md'].includes(value)
      },
      default: 'sm',
    },

    supportingText: {
      type: String,
      default: undefined,
    },

    v: {
      type: Object,
      default: () => {},
    },

    value: {
      type: Object,
      default: () => {},
    },
  })

  // DATA
  const KEY = uuidv4()
  const dropdownId = `dropdown-${KEY}`
  const dropdownRef = ref(null)
  const inputRef = ref(null)
  const selectedLabel = ref(null)

  // METHODS
  function doClear() {
    model.value = null
  }

  /**
   * when the dropdown gets closed and there's no valid model, clear the selectedLabel/input
   */
  async function onClose() {
    await nextTick()

    if (!model.value) {
      selectedLabel.value = null
    }
  }

  /**
   * handle keydown-events to set focus to opened dropdown menu
   */
  function onKeyDown(event) {
    if (dropdownRef.value.isOpen) {
      switch (event.code) {
        case 'Enter':
        case 'NumpadEnter':
          selectItem(props.results[0])
          dropdownRef.value.close()
          event.preventDefault()
          break

        case 'Escape':
          dropdownRef.value.close()
          event.preventDefault()
          break

        case 'Tab':
        case 'ArrowUp':
        case 'ArrowDown':
          dropdownRef.value.setFocus()
          event.preventDefault()
          break
      }
    }
  }

  /**
   * select item and then set focus on the input-field (to restore tabbing order)
   */
  function selectItem(item) {
    selectedLabel.value = item.label
    if (model.value === undefined || model.value === null) {
      model.value = item.value
    } else {
      ReactivityUtil.reAssign(model.value, item.value)
    }

    inputRef.value.inputRef.focus()
  }

  const search = debounce(_search, DEFAULT_FORM_DEBOUNCE_TIME)
  function _search(event) {
    model.value = null
    props.v?.$reset()

    emit('search', event.target.value.replace(/-+/g, '-'))
  }

  // WATCHERS
  watch(
    model,
    value => {
      if (!value) return
      selectedLabel.value = value[props.labelKey]
    },
    { immediate: true, deep: true }
  )

  watch(props.results, values => {
    if (values.length > 0) {
      dropdownRef.value.open()
    } else {
      dropdownRef.value.close()
    }
  })

  // LIFECYCLE HOOKS
  onMounted(() => {
    if (props.value && props.value[props.labelKey]) {
      selectedLabel.value = props.value[props.labelKey]
    }
  })
</script>

<style scoped>
.location {
  position: relative;

  .icon {
    color: var(--on-surface);
  }
}
</style>
